/**
 * Due-date utilities for Chèques & Traites.
 * Pure functions — no I/O.
 */

import type { DueDateStatus } from './types';

const DUE_SOON_DAYS = 7;

/**
 * Number of days from `today` until `dueDate`.
 * Negative means overdue.
 */
export function daysUntilDue(dueDate: string, today: Date = new Date()): number {
  const due = parseDateISO(dueDate);
  const todayMidnight = midnightUTC(today);
  const diffMs = due.getTime() - todayMidnight.getTime();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

/**
 * Returns a categorised status for the due date.
 *
 *   overdue   — past
 *   due_soon  — 0..DUE_SOON_DAYS days away (inclusive)
 *   upcoming  — DUE_SOON_DAYS+1..30 days away
 *   future    — more than 30 days away
 */
export function getDueDateStatus(dueDate: string, today: Date = new Date()): DueDateStatus {
  const days = daysUntilDue(dueDate, today);
  if (days < 0) return 'overdue';
  if (days <= DUE_SOON_DAYS) return 'due_soon';
  if (days <= 30) return 'upcoming';
  return 'future';
}

// ---- helpers ----

function parseDateISO(isoDate: string): Date {
  // Accepts "YYYY-MM-DD"
  const [y, m, d] = isoDate.split('-').map(Number);
  // Treat as UTC midnight to avoid timezone shifts
  return new Date(Date.UTC(y!, m! - 1, d!));
}

function midnightUTC(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
}
